'use client';

import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { Play, Pause, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MomentoVoice {
  id: string;
  name: string;
  description: string;
  avatar: string;
  preview?: string;
}

export const MOMENTO_VOICES: MomentoVoice[] = [
  {
    id: 'Dennis',
    name: 'Kiefer',
    description: "Let's capture your story",
    avatar: '/voices/kiefer.png',
  },
  {
    id: 'Ashley',
    name: 'Tessa',
    description: 'Ready to share something great?',
    avatar: '/voices/tessa.png',
  },
];

// Helper to get voice by name for direct sidebar access
export const getVoiceByName = (name: string): MomentoVoice | undefined => {
  return MOMENTO_VOICES.find(v => v.name === name);
};

interface VoiceSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectVoice: (voice: MomentoVoice) => void;
  isLoading?: boolean;
}

export function VoiceSelectionModal({
  open,
  onOpenChange,
  onSelectVoice,
  isLoading = false,
}: VoiceSelectionModalProps) {
  const [selectedVoice, setSelectedVoice] = useState<MomentoVoice>(MOMENTO_VOICES[0]);
  const [playingPreview, setPlayingPreview] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const preconnectRef = useRef<HTMLLinkElement | null>(null);

  // Preconnect to LiveKit server when modal opens (reduces connection latency by ~500ms)
  useEffect(() => {
    if (open && !preconnectRef.current) {
      const livekitUrl = process.env.NEXT_PUBLIC_LIVEKIT_URL;
      if (livekitUrl) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = livekitUrl.replace('wss://', 'https://');
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
        preconnectRef.current = link;
        console.log('[VoiceSelection] Preconnecting to LiveKit server');
      }
    }

    return () => {
      if (preconnectRef.current && !open) {
        preconnectRef.current.remove();
        preconnectRef.current = null;
      }
    };
  }, [open]);

  const playPreview = (voice: MomentoVoice) => {
    if (!voice.preview) return;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (playingPreview === voice.id) {
      setPlayingPreview(null);
      return;
    }

    const audio = new Audio(voice.preview);
    audioRef.current = audio;
    audio.play();
    setPlayingPreview(voice.id);
    audio.onended = () => {
      setPlayingPreview(null);
      audioRef.current = null;
    };
  };

  const handleSelect = () => {
    onSelectVoice(selectedVoice);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Choose Momento&apos;s Voice</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          {MOMENTO_VOICES.map((voice) => (
            <button
              key={voice.id}
              onClick={() => setSelectedVoice(voice)}
              disabled={isLoading}
              className={cn(
                'relative flex flex-col items-center p-4 rounded-lg border-2 transition-all hover:bg-accent/50',
                selectedVoice.id === voice.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="relative w-20 h-20 mb-2">
                <Image
                  src={voice.avatar}
                  alt={voice.name}
                  fill
                  className="rounded-full object-cover"
                  sizes="80px"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder-avatar.png';
                  }}
                />
                {selectedVoice.id === voice.id && (
                  <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                    <Check className="h-3 w-3" />
                  </div>
                )}
              </div>
              <span className="font-medium text-sm">{voice.name}</span>
              <span className="text-xs text-muted-foreground text-center mt-1">
                {voice.description}
              </span>
              {voice.preview && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2 h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    playPreview(voice);
                  }}
                >
                  {playingPreview === voice.id ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
              )}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              'Start Conversation'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
